<template>
  <div class="media-upload-wrapper media-upload-wrapper--single">
    <!-- 单图模式 -->
    <template v-if="!multiple">
      <div class="upload-trigger" :style="triggerStyle" @click="openPicker">
        <img v-if="modelValue" :src="modelValue" class="upload-img" :style="imgStyle" />
        <div v-else class="upload-placeholder" :style="placeholderStyle">
          <el-icon class="upload-icon"><Plus /></el-icon>
        </div>
      </div>
    </template>

    <!-- 多图模式 -->
    <template v-else>
      <div class="multi-upload-list">
        <div v-for="(url, idx) in modelValue || []" :key="idx" class="multi-item">
          <el-image :src="url" fit="cover" class="multi-preview" :preview-src-list="modelValue" preview-teleported />
          <div class="multi-remove" @click="removeImage(idx)">
            <el-icon><Close /></el-icon>
          </div>
        </div>
        <div v-if="!maxCount || (modelValue || []).length < maxCount" class="multi-add" @click="openPicker">
          <el-icon><Plus /></el-icon>
        </div>
      </div>
    </template>

    <!-- MediaPicker 弹窗 -->
    <MediaPicker
      v-model:visible="pickerVisible"
      :mode="multiple ? 'multiple' : 'single'"
      :title="pickerTitle"
      :max-count="maxCount"
      @confirm="handleConfirm"
    />

    <!-- 推荐尺寸提示 -->
    <div v-if="tip" class="upload-tip">{{ tip }}</div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import MediaPicker from './MediaPicker.vue';

const props = defineProps({
  modelValue: { type: [String, Array], default: null },
  multiple: { type: Boolean, default: false },
  title: { type: String, default: '选择素材' },
  maxCount: { type: Number, default: 0 }, // 多选最大可选数量（0=不限）
  width: { type: [String, Number], default: 200 },
  height: { type: [String, Number], default: 120 },
  fit: { type: String, default: 'cover' }, // cover | contain
  tip: { type: String, default: '' }, // 推荐尺寸提示文案，为空则不显示
});
const emit = defineEmits(['update:modelValue']);
const pickerVisible = ref(false);
const pickerTitle = ref(props.title);

const openPicker = () => {
  pickerTitle.value = props.multiple ? '选择多张素材' : props.title;
  pickerVisible.value = true;
};

const handleConfirm = (items) => {
  if (props.multiple) {
    // 多选：追加 URL
    const urls = items.map(i => i.url);
    const existing = Array.isArray(props.modelValue) ? [...props.modelValue] : [];
    emit('update:modelValue', [...existing, ...urls]);
  } else {
    // 单选：直接设 URL
    const url = items?.url || null;
    emit('update:modelValue', url);
  }
};

const removeImage = (idx) => {
  if (!Array.isArray(props.modelValue)) return;
  const list = [...props.modelValue];
  list.splice(idx, 1);
  emit('update:modelValue', list);
};

const toSize = (v) => (typeof v === 'number' ? v + 'px' : v);
const triggerStyle = computed(() => ({
  width: '100%',
  maxWidth: toSize(props.width),
  height: toSize(props.height),
}));
const imgStyle = computed(() => ({ width: '100%', height: '100%', maxWidth: '100%', objectFit: props.fit }));
const placeholderStyle = computed(() => ({ width: '100%', height: '100%' }));
</script>

<style scoped>
.media-upload-wrapper {
  display: block;
  width: 100%;
  min-width: 0;
}
/* 推荐尺寸提示 */
.upload-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: #909399;
  word-break: break-all;
}
.upload-trigger {
  display: block;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  padding: 4px;
  transition: border-color 0.2s;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;
}
.upload-trigger:hover {
  border-color: #409eff;
}
.upload-img {
  display: block;
  border-radius: 4px;
  max-width: 100%;
}
.upload-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}
.upload-icon {
  font-size: 28px;
  color: #8c939d;
}

/* 多图模式 */
.multi-upload-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.multi-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
}
.multi-preview {
  width: 100px;
  height: 100px;
  display: block;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
}
.multi-remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  opacity: 0;
  transition: opacity 0.2s;
}
.multi-item:hover .multi-remove {
  opacity: 1;
}
.multi-add {
  width: 100px;
  height: 100px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #8c939d;
  font-size: 24px;
  transition: border-color 0.2s;
}
.multi-add:hover {
  border-color: #409eff;
  color: #409eff;
}
</style>
